import { throttle } from 'lodash'

import { QuillInstance, QuillStatic } from '../../types'

import {
  CLONED_CONTENTS_CLASS_NAME,
  DICTIONARY_HIGHLIGHT_CLASS_NAME,
  DICTIONARY_HIGHLIGHT_MODULE_NAME,
  REGISTERED_WORD_CLASS_NAME,
  SELECTED_WORD_CLASS_NAME,
} from './constants'
import { DictionaryHighlightOptions } from './types'

const isSameDictionary = (
  a: {
    [key: string]: string
  },
  b: {
    [key: string]: string
  },
) => {
  const aKeys = Object.keys(a).sort()
  const bKeys = Object.keys(b).sort()

  if (aKeys.length !== bKeys.length) {
    return false
  }

  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) {
      return false
    }
  }

  return true
}

const findRegisteredWordElFromPoint = (x: number, y: number) => {
  const elements = document.elementsFromPoint(x, y)

  return elements?.find(
    (el) =>
      el.classList.contains(DICTIONARY_HIGHLIGHT_CLASS_NAME) &&
      el.classList.contains(REGISTERED_WORD_CLASS_NAME),
  )
}

export const registerDictionaryHighlightModule = (Quill: QuillStatic) => {
  class DictionaryHighlightModule {
    #quill: QuillInstance

    /**
     * .ql-editor
     */
    #rootEl: HTMLElement

    #clonedContentsRegisteredEl!: HTMLElement

    #clonedContentsSelectedEl!: HTMLElement

    #options: DictionaryHighlightOptions

    #dictionary: {
      [key: string]: string
    } = {}

    #selectedWord: string | undefined

    /**
     * 클론 오버레이 방식
     */
    constructor(
      quill: QuillInstance,
      options: DictionaryHighlightOptions = {},
    ) {
      this.#quill = quill
      this.#rootEl = quill.root
      this.#options = options

      this.#initClonedContentsEls()
      this.#listenTextChange()
      this.#observeDictionary()
      this.#observeSelectedWord()
      this.#listenPointerEvents()
    }

    /**
     * 초기화
     */
    #initClonedContentsEls = () => {
      const rootEl = this.#rootEl

      const parentEl = rootEl.parentElement

      const foundClonedContentsSelectedEl =
        parentEl?.querySelector<HTMLElement>(
          `.${CLONED_CONTENTS_CLASS_NAME}.${SELECTED_WORD_CLASS_NAME}`,
        )

      if (!!foundClonedContentsSelectedEl) {
        this.#clonedContentsSelectedEl = foundClonedContentsSelectedEl
      } else {
        const createdContentsSelectedEl = document.createElement('div')

        createdContentsSelectedEl.classList.add('ql-editor')
        createdContentsSelectedEl.classList.add(CLONED_CONTENTS_CLASS_NAME)
        createdContentsSelectedEl.classList.add(SELECTED_WORD_CLASS_NAME)

        this.#clonedContentsSelectedEl = createdContentsSelectedEl

        rootEl.insertAdjacentElement('beforebegin', createdContentsSelectedEl)
      }

      const foundClonedContentsRegisteredEl =
        parentEl?.querySelector<HTMLElement>(
          `.${CLONED_CONTENTS_CLASS_NAME}.${REGISTERED_WORD_CLASS_NAME}`,
        )

      if (!!foundClonedContentsRegisteredEl) {
        this.#clonedContentsRegisteredEl = foundClonedContentsRegisteredEl
      } else {
        const createdContentsRegisteredEl = document.createElement('div')

        createdContentsRegisteredEl.classList.add('ql-editor')
        createdContentsRegisteredEl.classList.add(CLONED_CONTENTS_CLASS_NAME)
        createdContentsRegisteredEl.classList.add(REGISTERED_WORD_CLASS_NAME)

        this.#clonedContentsRegisteredEl = createdContentsRegisteredEl

        rootEl.insertAdjacentElement('beforebegin', createdContentsRegisteredEl)
      }
    }

    /**
     * 현재 모듈이 활성화되어 있는지 확인
     */
    get #isDestroyed() {
      const rootEl = this.#rootEl
      /**
       * 에디터 엘리멘트가 body에 마운트 되어 있는지로 에디터가 살아있는 상태인지 확인
       */
      return !rootEl.closest('body')
    }

    /**
     * .ql-container
     */
    get #parentEl() {
      return this.#rootEl.parentElement
    }

    /**
     * 사전 데이터가 갱신되었는지 확인
     */
    #getDictionary = () => {
      return this.#options.getDictionary?.() ?? {}
    }

    #getSelectedWord = () => {
      return this.#options.getSelectedWord?.()
    }

    /**
     * 사전 데이터 갱신
     */
    #refreshDictionary = () => {
      const dictionary = this.#getDictionary()

      if (isSameDictionary(dictionary, this.#dictionary)) {
        return
      }

      this.#dictionary = dictionary
    }

    /**
     * 선택된 단어 갱신
     */
    #refreshSelectedWord = () => {
      const selectedWord = this.#getSelectedWord()

      if (selectedWord === this.#selectedWord) {
        return
      }

      this.#selectedWord = selectedWord
    }

    /**
     * 일정 시간 간격으로 등록된 단어 하이라이트 그리기
     */
    #observeDictionary = () => {
      const intervalId = setInterval(() => {
        /**
         * 에디터 엘리멘트가 body에 마운트 되어 있는지로 에디터가 살아있는 상태인지 확인
         */
        const isDestroyed = this.#isDestroyed

        if (isDestroyed) {
          console.log('destroyed')
          clearInterval(intervalId)
          return
        }

        const dict = this.#getDictionary()

        if (isSameDictionary(dict, this.#dictionary)) {
          return
        }

        this.#drawHighlightRegisteredWords()
      }, 200)
    }

    /**
     * 일정 시간 간격으로 선택된 단어 하이라이트 그리기
     */
    #observeSelectedWord = () => {
      const intervalId = setInterval(() => {
        /**
         * 에디터 엘리멘트가 body에 마운트 되어 있는지로 에디터가 살아있는 상태인지 확인
         */
        const isDestroyed = this.#isDestroyed

        if (isDestroyed) {
          clearInterval(intervalId)
          return
        }

        const selectedWord = this.#getSelectedWord()

        if (selectedWord === this.#selectedWord) {
          return
        }

        this.#drawHighlightSelectedWord()
      }, 200)
    }

    /**
     * 텍스트 변경 감지 후 등록된 단어 하이라이트 그리기
     */
    #listenTextChange = () => {
      const quill = this.#quill
      quill.on('text-change', () => {
        this.#drawHighlightRegisteredWords()
        this.#drawHighlightSelectedWord()
      })
    }

    /**
     * 복제된 루트 엘리멘트 얻기
     */
    #cloneSanitizedRootEl = () => {
      const rootEl = this.#rootEl

      const clonedRootEl = rootEl.cloneNode(true) as HTMLElement

      /**
       * 클론된 엘리먼트에서 id 제거\
       * 아이디 중복 방지
       */
      clonedRootEl.querySelectorAll('[id]').forEach((el) => {
        el.removeAttribute('id')
      })

      /**
       * 클론된 엘리멘트에서 일시정지 요소의 태그를 div에서 span으로 변경\
       * div가 p 아래에 위치할 수 없어서 이대로 두면 강제로 div가 p 밖으로 나감\
       * 그러면 일시정지 요소가 줄바꿈 되고 이어서 나오는 하이라이트 요소의 위치가 틀어짐
       */
      clonedRootEl
        .querySelectorAll<HTMLElement>('div:has(.pauseButton)')
        .forEach((el) => {
          const spanEl = document.createElement('span')
          spanEl.classList.add('pauseButton')
          spanEl.dataset.wordtype = 'pause'
          spanEl.innerText = el.innerText

          el.replaceWith(spanEl)
        })

      /**
       * 클론된 엘리멘트에서 u 태그 제거\
       * 기존 코드에서 언더라인 포맷이 활성화 되어 있음\
       * 혹시 언더라인이 포함된 텍스트가 있을 수 있기 때문에 언더라인은 제거함\
       * 나중에 다른 포맷 등이 추가/삭제/변경되면 이 부분을 수정해야 함
       */
      clonedRootEl.querySelectorAll('u').forEach((el) => {
        el.replaceWith(el.textContent || '')
      })
      /**
       * 포맷 제거 단계에서 쪼개진 텍스트 노드를 병합시킴
       */
      clonedRootEl.normalize()

      return clonedRootEl
    }

    /**
     * 등록된 단어 하이라이트 그리기
     */
    #drawHighlightRegisteredWords = throttle(() => {
      const clonedContentsRegisteredEl = this.#clonedContentsRegisteredEl

      this.#refreshDictionary()

      const dict = this.#dictionary

      const words = Object.keys(dict)

      const clonedRootEl = this.#cloneSanitizedRootEl()

      const clonedPs = [...clonedRootEl.querySelectorAll('p').values()]

      for (const word of words) {
        for (const p of clonedPs) {
          const childNodes = [...p.childNodes.values()]

          for (const childNode of childNodes) {
            /**
             * 순수한 텍스트 노드만 처리한다.
             */
            if (childNode.nodeType !== Node.TEXT_NODE) {
              continue
            }

            let textNode = childNode as Text
            let start = (textNode.textContent || '').indexOf(word)
            const length = word.length

            while (start >= 0) {
              const center = textNode.splitText(start)
              const right = center.splitText(length)

              const highlightEl = document.createElement('span')
              highlightEl.classList.add(DICTIONARY_HIGHLIGHT_CLASS_NAME)
              highlightEl.classList.add(REGISTERED_WORD_CLASS_NAME)
              highlightEl.textContent = center.textContent

              center.replaceWith(highlightEl)

              textNode = right
              start = (right.textContent || '').indexOf(word)
            }
          }
        }
      }

      clonedContentsRegisteredEl.innerHTML = clonedRootEl.innerHTML
    }, 1000)

    /**
     * 선택된 단어 하이라이트 그리기
     */
    #drawHighlightSelectedWord = throttle(() => {
      const clonedContentsSelectedEl = this.#clonedContentsSelectedEl

      this.#refreshSelectedWord()

      const selectedWord = this.#selectedWord

      /**
       * 선택된 단어가 없으면 공란으로 채움
       */
      if (!selectedWord) {
        clonedContentsSelectedEl.innerHTML = ''
        return
      }

      const clonedRootEl = this.#cloneSanitizedRootEl()

      const clonedPs = [...clonedRootEl.querySelectorAll('p').values()]

      for (const p of clonedPs) {
        const childNodes = [...p.childNodes.values()]

        for (const childNode of childNodes) {
          /**
           * 순수한 텍스트 노드만 처리한다.
           */
          if (childNode.nodeType !== Node.TEXT_NODE) {
            continue
          }

          let textNode = childNode as Text
          let start = (textNode.textContent || '').indexOf(selectedWord)
          const length = selectedWord.length

          while (start >= 0) {
            const center = textNode.splitText(start)
            const right = center.splitText(length)

            const highlightEl = document.createElement('span')
            highlightEl.classList.add(DICTIONARY_HIGHLIGHT_CLASS_NAME)
            highlightEl.classList.add(SELECTED_WORD_CLASS_NAME)
            highlightEl.textContent = center.textContent

            center.replaceWith(highlightEl)

            textNode = right
            start = (right.textContent || '').indexOf(selectedWord)
          }
        }
      }

      clonedContentsSelectedEl.innerHTML = clonedRootEl.innerHTML
    }, 1000)

    /**
     * 포인터 이벤트 리스너 등록\
     * 마우스 이동 시 등록된 단어에 hover 효과 주기 및 마우스 포인터 변경 처리\
     * 사전에 등록된 단어 클릭 시 해당 단어를 편집할 수 있도록 하는 이벤트 처리
     */
    #listenPointerEvents = () => {
      this.#rootEl.addEventListener('pointermove', this.#handlePointerMove)
      this.#rootEl.addEventListener('pointerdown', this.handlePointerDown)
    }

    /**
     * 마우스 이동 시 등록된 단어에 hover 효과 주기 및 마우스 포인터 변경 처리
     */
    #handlePointerMove = (e: PointerEvent) => {
      if (this.#isDestroyed) {
        this.#rootEl.removeEventListener('pointermove', this.#handlePointerMove)
        return
      }

      const foundRegisteredWordEl = findRegisteredWordElFromPoint(
        e.clientX,
        e.clientY,
      )

      if (!!foundRegisteredWordEl) {
        /**
         * rootEl이 아닌 parentEl의 클래스를 변경하는 이유:\
         * rootEl의 클래스를 변경하면 드래그 중일 때 셀렉션이 초기화 되는 현상이 있음\
         * 그래서 rootEl보다 한단계 상위인 parentEl의 클래스를 변경하여 해결함
         */
        this.#parentEl?.classList.add('hover')
      } else {
        this.#parentEl?.classList.remove('hover')
      }
    }

    /**
     * 사전에 등록된 단어 클릭 시 해당 단어를 편집할 수 있도록 하는 이벤트 처리
     *
     * 단순히 클릭 이벤트를 사용하지 않고 pointerdown과 click 이벤트를 함께 사용한 이유:\
     * 사전에 등록된 특정 단어를 바로 클릭하지 않고\
     * 해당 단어의 바깥에서부터 드래그를 시작하여 해당 단어에서 끝나는 경우\
     * 사용자가 느끼기에는 해당 단어를 클릭한 것이 아니지만\
     * 실제 동작으로는 클릭한 것으로 처리되는 현상이 있음\
     * 그래서 마우스 다운 시 단어에서 다운됐는지 확인하고\
     * 마우스 클릭 이벤트 리스너를 지정하고\
     * 최종적으로 마우스 클릭 발생 시\
     * 다운 시와 클릭 시의 단어가 같은지 확인하여 최종 처리하도록 함
     */
    handlePointerDown = (pointerDownEvent: PointerEvent) => {
      if (this.#isDestroyed) {
        this.#rootEl.removeEventListener('pointerdown', this.handlePointerDown)
        return
      }

      const foundRegisteredWordElOnPointerDown = findRegisteredWordElFromPoint(
        pointerDownEvent.clientX,
        pointerDownEvent.clientY,
      )

      if (!foundRegisteredWordElOnPointerDown) {
        return
      }

      /**
       * trim 필요한가??
       */
      const word = foundRegisteredWordElOnPointerDown.textContent

      if (!word) {
        return
      }

      const handleMouseClick = (mouseClickEvent: MouseEvent) => {
        this.#rootEl.removeEventListener('click', handleMouseClick)

        const foundRegisteredWordElOnMouseClick = findRegisteredWordElFromPoint(
          mouseClickEvent.clientX,
          mouseClickEvent.clientY,
        )

        if (
          foundRegisteredWordElOnPointerDown !==
          foundRegisteredWordElOnMouseClick
        ) {
          return
        }

        const range = document.createRange()
        range.selectNode(foundRegisteredWordElOnMouseClick)

        this.#options.onClickRegisteredWord?.(
          mouseClickEvent,
          word,
          range.getBoundingClientRect(),
        )
      }

      this.#rootEl.addEventListener('click', handleMouseClick)
    }
  }

  Quill.register(
    `modules/${DICTIONARY_HIGHLIGHT_MODULE_NAME}`,
    DictionaryHighlightModule,
    true,
  )

  return DictionaryHighlightModule
}
